import { NewProjectStep } from 'domains/projects/types';
import { createRouteConfig } from 'modules/router/utils/createRouteConfig';
import { useQueryParams } from 'modules/common/hooks/useQueryParams';

import { PROJECTS_PATH, PROJECT_INDEX_QUERY } from './const';

const NEW_PROJECT_V2_PATH = `${PROJECTS_PATH}new/`;
const EDIT_PROJECT_V2_PATH = `${PROJECTS_PATH}edit/`;

// Step path mappings
const stepPaths: Record<NewProjectStep, string> = {
  [NewProjectStep.General]: 'general',
  [NewProjectStep.Chains]: 'chains',
  [NewProjectStep.Whitelist]: 'whitelists',
};

const stepValues: Record<string, NewProjectStep> = {
  general: NewProjectStep.General,
  chains: NewProjectStep.Chains,
  whitelists: NewProjectStep.Whitelist,
};

export const projectsV2RoutesConfig = createRouteConfig(
  {
    // New project flow routes
    newProjectStep: {
      path: `${NEW_PROJECT_V2_PATH}:step/`,
      generatePath: (step: NewProjectStep) => {
        return `${NEW_PROJECT_V2_PATH}${stepPaths[step]}`;
      },
    },
    newProjectGeneral: {
      path: `${NEW_PROJECT_V2_PATH}general/`,
      generatePath: () => `${NEW_PROJECT_V2_PATH}general/`,
    },
    newProjectChains: {
      path: `${NEW_PROJECT_V2_PATH}chains/`,
      generatePath: () => `${NEW_PROJECT_V2_PATH}chains/`,
    },
    newProjectWhitelists: {
      path: `${NEW_PROJECT_V2_PATH}whitelists/`,
      generatePath: () => `${NEW_PROJECT_V2_PATH}whitelists/`,
    },

    // Edit project flow routes
    editProjectStep: {
      path: `${EDIT_PROJECT_V2_PATH}:step/`,
      generatePath: (projectIndex: number, step: NewProjectStep) => {
        const params = new URLSearchParams({
          [PROJECT_INDEX_QUERY]: projectIndex.toString(),
        });

        return `${EDIT_PROJECT_V2_PATH}${
          stepPaths[step]
        }/?${params.toString()}`;
      },
    },
    editProjectGeneral: {
      path: `${EDIT_PROJECT_V2_PATH}general/`,
      generatePath: (projectIndex: number) => {
        const params = new URLSearchParams({
          [PROJECT_INDEX_QUERY]: projectIndex.toString(),
        });

        return `${EDIT_PROJECT_V2_PATH}general/?${params.toString()}`;
      },
    },
    editProjectChains: {
      path: `${EDIT_PROJECT_V2_PATH}chains/`,
      generatePath: (projectIndex: number) => {
        const params = new URLSearchParams({
          [PROJECT_INDEX_QUERY]: projectIndex.toString(),
        });

        return `${EDIT_PROJECT_V2_PATH}chains/?${params.toString()}`;
      },
    },
    editProjectWhitelists: {
      path: `${EDIT_PROJECT_V2_PATH}whitelists/`,
      generatePath: (projectIndex: number) => {
        const params = new URLSearchParams({
          [PROJECT_INDEX_QUERY]: projectIndex.toString(),
        });

        return `${EDIT_PROJECT_V2_PATH}whitelists/?${params.toString()}`;
      },
    },

    // Fallback route that redirects to general step
    newProject: {
      path: NEW_PROJECT_V2_PATH,
      generatePath: () => NEW_PROJECT_V2_PATH,
    },
    editProject: {
      path: `${PROJECTS_PATH}edit/general/`,
      generatePath: (projectIndex: number) => {
        const params = new URLSearchParams({
          [PROJECT_INDEX_QUERY]: projectIndex.toString(),
        });

        return `${PROJECTS_PATH}edit/general/?${params.toString()}`;
      },
      useParams: () => {
        const query = useQueryParams();

        const rawProjectIndex = query.get(PROJECT_INDEX_QUERY);
        const projectIndex =
          rawProjectIndex === null || rawProjectIndex === ''
            ? undefined
            : Number(rawProjectIndex);

        return {
          projectIndex:
            projectIndex !== undefined &&
            Number.isInteger(projectIndex) &&
            projectIndex >= 0
              ? projectIndex
              : undefined,
        };
      },
    },
  },
  PROJECTS_PATH,
);

// Helper functions
export const getStepFromPath = (stepParam?: string): NewProjectStep => {
  if (!stepParam || !stepValues[stepParam]) {
    return NewProjectStep.General;
  }

  return stepValues[stepParam];
};

export const getNextStep = (
  currentStep: NewProjectStep,
): NewProjectStep | null => {
  switch (currentStep) {
    case NewProjectStep.General:
      return NewProjectStep.Chains;
    case NewProjectStep.Chains:
      return NewProjectStep.Whitelist;
    case NewProjectStep.Whitelist:
      return null; // Last step
    default:
      return NewProjectStep.General;
  }
};

export const getPreviousStep = (
  currentStep: NewProjectStep,
): NewProjectStep | null => {
  switch (currentStep) {
    case NewProjectStep.General:
      return null; // First step
    case NewProjectStep.Chains:
      return NewProjectStep.General;
    case NewProjectStep.Whitelist:
      return NewProjectStep.Chains;
    default:
      return null;
  }
};
